import { useState, useRef, useEffect, useCallback, type ReactNode, type ChangeEvent, type InputHTMLAttributes } from 'react';

// ─── Formatters ──────────────────────────────────────────────────────────────

export const fmtGBP = (n: number, dp = 2): string =>
  new Intl.NumberFormat('en-GB', {
    style: 'currency', currency: 'GBP',
    minimumFractionDigits: dp, maximumFractionDigits: dp,
  }).format(Object.is(n, -0) || Math.abs(n) < 5e-3 ? 0 : n).replace(/\u00A0/g, ' ');

export const fmtNum = (n: number, dp = 0): string =>
  new Intl.NumberFormat('en-GB', { minimumFractionDigits: dp, maximumFractionDigits: dp }).format(n);

const MONTHS = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];

export const fmtDate = (iso: string, opts?: Intl.DateTimeFormatOptions): string => {
  const d = new Date(iso);
  const day = String(d.getUTCDate()).padStart(2, '0');
  const mon = MONTHS[d.getUTCMonth()];
  const yr  = d.getUTCFullYear();
  return opts && !opts.year ? `${day} ${mon}` : `${day} ${mon} ${yr}`;
};

export const fmtSortCode = (s?: string | null): string => {
  if (!s) return '—';
  const d = s.replace(/\D/g, '');
  return d.length === 6 ? `${d.slice(0,2)}-${d.slice(2,4)}-${d.slice(4,6)}` : s;
};

// As-you-type mask for a sort-code text input: keeps only digits, caps at 6, inserts dashes.
export const maskSortCodeInput = (raw: string): string => {
  const d = raw.replace(/\D/g, '').slice(0, 6);
  return [d.slice(0, 2), d.slice(2, 4), d.slice(4, 6)].filter(Boolean).join('-');
};

export const fmtDateTime = (iso: string): string => {
  const d = new Date(iso);
  const day = String(d.getDate()).padStart(2, '0');
  const mon = MONTHS[d.getMonth()];
  const yr  = d.getFullYear();
  const hh  = String(d.getHours()).padStart(2, '0');
  const mm  = String(d.getMinutes()).padStart(2, '0');
  return `${day} ${mon} ${yr}, ${hh}:${mm}`;
};

export const relTime = (iso: string): string => {
  const diff = (Date.now() - new Date(iso).getTime()) / 1000;
  if (diff < 60) return 'just now';
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  if (diff < 86400 * 30) return `${Math.floor(diff / 86400)}d ago`;
  if (diff < 86400 * 365) return `${Math.floor(diff / 86400 / 30)}mo ago`;
  return `${Math.floor(diff / 86400 / 365)}y ago`;
};

// ─── Inputs ──────────────────────────────────────────────────────────────────

// Strip a raw number-input string down to at most one leading "-" and one ".",
// digits only otherwise (mirrors what a native <input type="number"> would accept).
const cleanNumberString = (raw: string, allowDecimal: boolean): string => {
  let s = raw.replace(/,/g, '');
  const neg = s.startsWith('-');
  s = s.replace(/-/g, '');
  const firstDot = s.indexOf('.');
  if (firstDot !== -1) s = s.slice(0, firstDot + 1) + s.slice(firstDot + 1).replace(/\./g, '');
  s = s.replace(/[^\d.]/g, '');
  if (!allowDecimal) s = s.replace(/\./g, '');
  return (neg ? '-' : '') + s;
};

// Comma-group the integer part of a cleaned numeric string for display, e.g. "123456.7" -> "123,456.7".
const groupNumberString = (raw: string): string => {
  if (!raw) return raw;
  const neg = raw.startsWith('-') ? '-' : '';
  const body = neg ? raw.slice(1) : raw;
  const [intPart, decPart] = body.split('.');
  const grouped = intPart.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  return neg + grouped + (decPart !== undefined ? '.' + decPart : '');
};

// Text input for monetary/numeric values that live-formats with comma thousand-separators
// as the user types (e.g. "123456.78" displays as "123,456.78"), while the value passed to
// onChange stays a plain unformatted numeric string — a drop-in replacement for
// `<input type="number" value={s} onChange={e => setS(e.target.value)} />` wherever the
// value is shown to the user, not just consumed as a machine-parsed number.
export function NumberInput({ value, onChange, allowDecimal = true, ...rest }: {
  value: string;
  onChange: (raw: string) => void;
  allowDecimal?: boolean;
} & Omit<InputHTMLAttributes<HTMLInputElement>, 'value' | 'onChange' | 'type'>) {
  const ref = useRef<HTMLInputElement>(null);
  const pendingCaret = useRef<number | null>(null);

  const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
    const input = e.target;
    const caretPos = input.selectionStart ?? input.value.length;
    const meaningfulBeforeCaret = input.value.slice(0, caretPos).replace(/[^\d.-]/g, '').length;

    const cleaned = cleanNumberString(input.value, allowDecimal);
    onChange(cleaned);

    const formatted = groupNumberString(cleaned);
    let count = 0, newCaret = formatted.length;
    for (let i = 0; i < formatted.length; i++) {
      if (/[\d.-]/.test(formatted[i])) count++;
      if (count === meaningfulBeforeCaret) { newCaret = i + 1; break; }
    }
    pendingCaret.current = newCaret;
  };

  useEffect(() => {
    if (pendingCaret.current != null && ref.current) {
      ref.current.setSelectionRange(pendingCaret.current, pendingCaret.current);
      pendingCaret.current = null;
    }
  });

  return (
    <input
      ref={ref}
      type="text"
      inputMode={allowDecimal ? 'decimal' : 'numeric'}
      value={groupNumberString(value)}
      onChange={handleChange}
      {...rest}
    />
  );
}

// ─── Icons ───────────────────────────────────────────────────────────────────

export function PencilIcon() {
  return (
    <svg width="13" height="13" viewBox="0 0 13 13" fill="none" stroke="currentColor" strokeWidth="1.8" aria-hidden="true">
      <path d="M9.5 1.5l2 2L4 11H2V9L9.5 1.5z" />
    </svg>
  );
}

// ─── Atoms ───────────────────────────────────────────────────────────────────

export function StatusDot({ kind }: { kind: string }) {
  return <span className={`status-dot status-${kind}`} aria-hidden="true" />;
}

export function Badge({ children, kind = 'neutral' }: { children: ReactNode; kind?: string }) {
  return <span className={`badge badge-${kind}`}>{children}</span>;
}

export function ConfirmDialog({ title, children, confirmLabel, busyLabel, busy, error, danger = true, onConfirm, onCancel }: {
  title: string; children: ReactNode; confirmLabel: string; busyLabel?: string; busy?: boolean; error?: string | null;
  danger?: boolean; onConfirm: () => void; onCancel: () => void;
}) {
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape' && !busy) onCancel(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [busy, onCancel]);
  return (
    <div className="confirm-overlay" onClick={() => { if (!busy) onCancel(); }}>
      <div className="confirm-box" role="alertdialog" aria-modal="true" aria-labelledby="confirm-title" onClick={e => e.stopPropagation()}>
        <h2 id="confirm-title" className="confirm-title">{title}</h2>
        <div className="confirm-body">{children}</div>
        {error && <div className="confirm-err" role="alert">{error}</div>}
        <div className="confirm-actions">
          <button type="button" className="btn" onClick={onCancel} disabled={busy} autoFocus>Cancel</button>
          <button type="button" className={`btn ${danger ? 'btn-danger' : 'btn-primary'}`} onClick={onConfirm} disabled={busy}>
            {busy ? (busyLabel ?? 'Working…') : confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
}

export function KBD({ children }: { children: ReactNode }) {
  return <span className="kbd">{children}</span>;
}

export function Avatar({ initials, size = 24, color }: { initials: string; size?: number; color?: string }) {
  return (
    <span
      className="avatar"
      style={{ width: size, height: size, fontSize: size * 0.4, background: color ?? '#e5e7eb' }}
    >
      {initials}
    </span>
  );
}

export function UtilBar({ value, max, height = 6, accent }: {
  value: number; max: number; height?: number; accent?: string;
}) {
  const pct = Math.max(0, Math.min(100, (value / max) * 100));
  return (
    <div className="util" style={{ height }}>
      <div className="util-fill" style={{ width: `${pct}%`, ...(accent ? { background: accent } : {}) }} />
    </div>
  );
}

export function SegmentedBar({ segments, max, height = 8, gap = 2, track }: {
  segments: { value: number; color: string; label?: string }[];
  max?: number;
  height?: number;
  gap?: number;
  track?: string;
}) {
  const denom = max ?? segments.reduce((s, seg) => s + seg.value, 0);
  return (
    <div style={{ display: 'flex', height, borderRadius: height / 2, overflow: 'hidden', gap, background: track ?? 'transparent' }}>
      {segments.map((seg, i) => (
        <div key={i} title={seg.label} style={{ width: `${denom > 0 ? (seg.value / denom) * 100 : 0}%`, background: seg.color, flexShrink: 0, transition: 'width 0.2s' }} />
      ))}
    </div>
  );
}

// ─── Inline icons ─────────────────────────────────────────────────────────────
// Only used where they communicate meaning — never decorative.

export const I = {
  search:   <svg viewBox="0 0 16 16" fill="none" width="1em" height="1em"><circle cx="7" cy="7" r="4.5" stroke="currentColor" strokeWidth="1.4"/><path d="m10.5 10.5 3 3" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round"/></svg>,
  chev:     <svg viewBox="0 0 16 16" fill="none" width="1em" height="1em"><path d="m6 4 4 4-4 4" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round"/></svg>,
  chevDown: <svg viewBox="0 0 16 16" fill="none" width="1em" height="1em"><path d="m4 6 4 4 4-4" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round"/></svg>,
  plus:     <svg viewBox="0 0 16 16" fill="none" width="1em" height="1em"><path d="M8 3.5v9M3.5 8h9" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round"/></svg>,
  more:     <svg viewBox="0 0 16 16" fill="currentColor" width="1em" height="1em"><circle cx="3.5" cy="8" r="1.2"/><circle cx="8" cy="8" r="1.2"/><circle cx="12.5" cy="8" r="1.2"/></svg>,
  ext:      <svg viewBox="0 0 16 16" fill="none" width="1em" height="1em"><path d="M6 4h6v6M12 4 5 11" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round"/></svg>,
  pencil:   <svg viewBox="0 0 16 16" fill="none" width="1em" height="1em"><path d="M3 13h3l7-7-3-3-7 7v3z" stroke="currentColor" strokeWidth="1.4" strokeLinejoin="round"/></svg>,
  alert:    <svg viewBox="0 0 16 16" fill="none" width="1em" height="1em"><path d="M8 2.5 1.5 13.5h13L8 2.5Z" stroke="currentColor" strokeWidth="1.4" strokeLinejoin="round"/><path d="M8 6.5v3M8 11.5h.01" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round"/></svg>,
  check:    <svg viewBox="0 0 16 16" fill="none" width="1em" height="1em"><path d="m3.5 8.5 3 3 6-7" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/></svg>,
  copy:     <svg viewBox="0 0 16 16" fill="none" width="1em" height="1em"><rect x="4.5" y="4.5" width="8" height="9" rx="1.2" stroke="currentColor" strokeWidth="1.3"/><path d="M3 11V3a.5.5 0 0 1 .5-.5H11" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round"/></svg>,
  chevL:    <svg viewBox="0 0 16 16" fill="none" width="1em" height="1em"><path d="m10 4-4 4 4 4" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round"/></svg>,
  chevR:    <svg viewBox="0 0 16 16" fill="none" width="1em" height="1em"><path d="m6 4 4 4-4 4" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round"/></svg>,
  doc:      <svg viewBox="0 0 16 16" fill="none" width="1em" height="1em"><path d="M4 2h6l3 3v9H4V2z" stroke="currentColor" strokeWidth="1.3" strokeLinejoin="round"/><path d="M9 2v4h4" stroke="currentColor" strokeWidth="1.3" strokeLinejoin="round"/></svg>,
} as const;

// ─── Layout helpers ───────────────────────────────────────────────────────────

export function PageHeader({ title, subtitle, actions, kpis }: {
  title: string;
  subtitle?: string;
  actions?: ReactNode;
  kpis?: ReactNode;
}) {
  return (
    <div className="page-hd">
      <div className="page-hd-row">
        <div>
          <h1 className="page-hd-title">{title}</h1>
          {subtitle && <div className="page-hd-sub">{subtitle}</div>}
        </div>
        <div className="page-hd-actions">{actions}</div>
      </div>
      {kpis && <div className="kpis-row">{kpis}</div>}
    </div>
  );
}

export function MiniKpi({ label, value, sub, tone = 'neutral' }: {
  label: string;
  value: ReactNode;
  sub?: string;
  tone?: 'neutral' | 'warn' | 'neg' | 'pos';
}) {
  return (
    <div className="mini-kpi" data-tone={tone}>
      <div className="mini-kpi-label">{label}</div>
      <div className="mini-kpi-value">{value}</div>
      {sub && <div className="mini-kpi-sub">{sub}</div>}
    </div>
  );
}

export function FilterBar({ children }: { children: ReactNode }) {
  return <div className="filter-bar">{children}</div>;
}

// ─── Pagination ───────────────────────────────────────────────────────────────

export function usePagination<T>(items: T[], pageSize = 10) {
  const [page, setPage] = useState(1);
  const prevItems = useRef(items);
  if (prevItems.current !== items) { prevItems.current = items; setPage(1); }

  const totalPages = Math.max(1, Math.ceil(items.length / pageSize));
  const safePage = Math.min(page, totalPages);
  const pageItems = items.slice((safePage - 1) * pageSize, safePage * pageSize);

  return {
    pageItems,
    page: safePage,
    totalPages,
    hasPrev: safePage > 1,
    hasNext: safePage < totalPages,
    prev: () => setPage((p) => Math.max(1, p - 1)),
    next: () => setPage((p) => Math.min(totalPages, p + 1)),
  };
}

export function Pager({ page, totalPages, hasPrev, hasNext, prev, next, count }: {
  page: number; totalPages?: number; hasPrev: boolean; hasNext: boolean;
  prev: () => void; next: () => void; count: number;
}) {
  // totalPages is only known for the client-side-sliced usePagination path; server-cursor
  // pagination (usePagedList) can't cheaply expose a total, so it's omitted there.
  if (totalPages !== undefined ? totalPages <= 1 : (!hasPrev && !hasNext && page === 1)) return null;
  return (
    <div className="pager">
      <button className="pager-btn" onClick={prev} disabled={!hasPrev}>{I.chevL} Prev</button>
      <span className="pager-info">
        {totalPages !== undefined ? <>Page {page} of {totalPages} · {count} rows</> : <>Page {page} · {count} rows</>}
      </span>
      <button className="pager-btn" onClick={next} disabled={!hasNext}>Next {I.chevR}</button>
    </div>
  );
}

// Server-cursor pagination for GraphQL connections shaped { items, nextToken }. `fetchPage` is
// re-invoked from page 1 whenever `deps` changes (e.g. a status filter or debounced search term),
// mirroring usePagination's reset-on-input-change behavior but round-tripping to the server
// instead of re-slicing an in-memory array.
export interface PagedListResult<T> {
  items: T[];
  loading: boolean;
  error: string | null;
  page: number;
  hasPrev: boolean;
  hasNext: boolean;
  prev: () => void;
  next: () => void;
  reset: () => void;
}

export function useDebouncedValue<T>(value: T, delayMs = 300): T {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const timer = setTimeout(() => setDebounced(value), delayMs);
    return () => clearTimeout(timer);
  }, [value, delayMs]);
  return debounced;
}

export function usePagedList<T>(
  fetchPage: (opts: { limit?: number; nextToken?: string }) => Promise<{ items: T[]; nextToken?: string }>,
  deps: unknown[],
  pageSize = 25,
): PagedListResult<T> {
  const [items, setItems] = useState<T[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [tokenHistory, setTokenHistory] = useState<(string | undefined)[]>([undefined]);
  const [pageIndex, setPageIndex] = useState(0);
  const [nextToken, setNextToken] = useState<string | undefined>(undefined);
  const fetchRef = useRef(fetchPage);
  fetchRef.current = fetchPage;

  const load = useCallback((token: string | undefined) => {
    setLoading(true);
    fetchRef.current({ limit: pageSize, nextToken: token })
      .then((p) => { setItems(p.items); setNextToken(p.nextToken); setError(null); })
      .catch((e: Error) => setError(e.message))
      .finally(() => setLoading(false));
  }, [pageSize]);

  useEffect(() => {
    setTokenHistory([undefined]);
    setPageIndex(0);
    load(undefined);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);

  function next() {
    if (!nextToken) return;
    setTokenHistory((h) => h.slice(0, pageIndex + 1).concat(nextToken));
    setPageIndex((i) => i + 1);
    load(nextToken);
  }

  function prev() {
    if (pageIndex === 0) return;
    const prevToken = tokenHistory[pageIndex - 1];
    setPageIndex((i) => i - 1);
    load(prevToken);
  }

  function reset() {
    setTokenHistory([undefined]);
    setPageIndex(0);
    load(undefined);
  }

  return {
    items, loading, error,
    page: pageIndex + 1,
    hasPrev: pageIndex > 0,
    hasNext: !!nextToken,
    prev, next, reset,
  };
}
